/**
 * Resizable Panel - Eliza Forge Design System
 *
 * Split-pane layouts with draggable dividers.
 * Thin DS wrappers around react-resizable-panels.
 */

import * as React from "react"
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type PanelGroupProps,
  type PanelProps,
  type PanelResizeHandleProps,
} from "react-resizable-panels"
import { cn } from "../../shared/lib/cn"

/* ============================================
   RESIZABLE PANEL GROUP
   ============================================ */

const ResizablePanelGroup = React.forwardRef<
  HTMLDivElement,
  PanelGroupProps & React.RefAttributes<HTMLDivElement>
>(({ className, ...props }, _ref) => (
  <PanelGroup
    className={cn("flex h-full w-full", className)}
    {...props}
  />
))
ResizablePanelGroup.displayName = "ResizablePanelGroup"

/* ============================================
   RESIZABLE PANEL
   ============================================ */

const ResizablePanel = Panel
ResizablePanel.displayName = "ResizablePanel"

/* ============================================
   RESIZABLE HANDLE
   ============================================ */

interface ResizableHandleProps extends Omit<PanelResizeHandleProps, "children"> {
  withHandle?: boolean
}

const ResizableHandle = React.forwardRef<
  HTMLDivElement,
  ResizableHandleProps & React.RefAttributes<HTMLDivElement>
>(({ className, withHandle = true, ...props }, _ref) => (
  <PanelResizeHandle
    className={cn(
      "relative flex items-center justify-center",
      "bg-gray-200 dark:bg-dark-border",
      "hover:bg-gray-300 dark:hover:bg-gray-600",
      "active:bg-eliza-red",
      "transition-colors duration-150",
      "data-[panel-group-direction=vertical]:h-1 data-[panel-group-direction=vertical]:w-full",
      "data-[panel-group-direction=horizontal]:h-full data-[panel-group-direction=horizontal]:w-1",
      className
    )}
    {...props}
  >
    {withHandle && (
      <div
        className={cn(
          "z-10 flex items-center justify-center rounded-sm",
          "bg-gray-300 dark:bg-gray-600 text-gray-400",
          "data-[panel-group-direction=vertical]:h-1 data-[panel-group-direction=vertical]:w-8",
          "data-[panel-group-direction=horizontal]:h-8 data-[panel-group-direction=horizontal]:w-1"
        )}
      >
        <GripIcon />
      </div>
    )}
  </PanelResizeHandle>
))
ResizableHandle.displayName = "ResizableHandle"

/* ============================================
   GRIP ICON — handle indicator
   ============================================ */

const GripIcon: React.FC = () => (
  <svg
    width="6"
    height="14"
    viewBox="0 0 6 14"
    fill="currentColor"
    className="opacity-50"
  >
    <circle cx="1.5" cy="3" r="1" />
    <circle cx="4.5" cy="3" r="1" />
    <circle cx="1.5" cy="7" r="1" />
    <circle cx="4.5" cy="7" r="1" />
    <circle cx="1.5" cy="11" r="1" />
    <circle cx="4.5" cy="11" r="1" />
  </svg>
)

export { ResizablePanelGroup, ResizablePanel, ResizableHandle }
export type { ResizableHandleProps }
